import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons'
import { ChartDataSets, ChartOptions } from 'chart.js'
import React, {
  FunctionComponent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Doughnut } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'

import { useAppSelector } from '../../../App/Hooks/reduxHooks'
import {
  aquamarineToPurple,
  beigeToGreen,
  beigeToPurple,
  blueToAquamarine,
  blueToBlue,
  blueToPurple,
  brownToBlue,
  darkPinkToRose,
  greenToBlue,
  greetToBrown,
  pinkToBeige,
  pinkToOrange,
  pinkToYellow,
  redToGreen,
  roseToPinkish,
  salmonToPink,
  violetToPurple,
  yellowToBlue,
  yellowToOrange,
  yellowToPurple,
} from '../../../App/Util/graphGradients/gradientColors'
import { graphGradients } from '../../../App/Util/graphGradients/graphGradients'
import { IUser } from '../../../User/DataStore/IUser'
import { DashBoardCardTitle } from '../styles'

import { Client } from './Client/Client'
import { IEmployeeReferenceStatistics } from './Client/IEmployeeReference'
import { IProps } from './IProps'
import {
  EmployeeReferenceCardContainer,
  InfoIcon,
  NoEmployeeLabel,
} from './style'

export const EmployeeReferenceCard: FunctionComponent<IProps> = ({
  from,
  to,
}) => {
  const [referenceArray, setReferenceArray] = useState<
    IEmployeeReferenceStatistics[]
  >([])
  const [usersOfEstablishment, setUsersOfEstablishment] = useState<IUser[]>([])
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
  })

  const [labels, chosenMinutes, totalMinutes] = useMemo(() => {
    const calculatedData: {
      [personUuid: string]: { name: string; chosenMinutes: number }
    } = {}
    for (const user of usersOfEstablishment) {
      calculatedData[user.uuid] = {
        name: user.firstName,
        chosenMinutes: 0,
      }
    }
    let subTotalMinutes = 0
    for (const referenceData of referenceArray) {
      if (!(referenceData.personUuid in calculatedData)) {
        continue
      }

      calculatedData[referenceData.personUuid].chosenMinutes +=
        referenceData.loyalty.chosenMinutes

      subTotalMinutes += referenceData.loyalty.chosenMinutes
    }

    const calculatedValues = Object.values(calculatedData)

    return [
      calculatedValues.map(calculatedValue => calculatedValue.name),
      calculatedValues.map(calculatedValue => calculatedValue.chosenMinutes),
      subTotalMinutes,
    ]
  }, [referenceArray, usersOfEstablishment])

  const chartRef = useRef<Doughnut | null>(null)
  const { t } = useTranslation()
  const { users, establishment } = useAppSelector(state => {
    return {
      users: state.users.list,
      establishment: state.establishment.active,
    }
  })

  const options: ChartOptions<'doughnut'> = {
    responsive: true,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          boxWidth: 10,
          boxHeight: 10,
        },
      },
    },
  }

  useEffect(() => {
    const chart = chartRef.current
    if (chart) {
      const gradients = graphGradients(chart.ctx, [
        blueToBlue,
        blueToPurple,
        pinkToYellow,
        pinkToBeige,
        yellowToOrange,
        yellowToBlue,
        brownToBlue,
        aquamarineToPurple,
        salmonToPink,
        pinkToOrange,
        beigeToGreen,
        blueToAquamarine,
        roseToPinkish,
        greenToBlue,
        redToGreen,
        beigeToPurple,
        violetToPurple,
        yellowToPurple,
        darkPinkToRose,
        greetToBrown,
      ])
      setChartData({
        labels,
        datasets: [
          {
            data: chosenMinutes,
            backgroundColor: gradients,
            borderWidth: 1,
          },
        ] as ChartDataSets,
      })
    }
  }, [labels, chosenMinutes, totalMinutes])

  useEffect(() => {
    if (users === usersOfEstablishment || !establishment) {
      return
    }

    setUsersOfEstablishment(
      users.filter(user => {
        if (user.establishmentId === establishment.id) {
          return user
        }
      }),
    )
  }, [users, establishment])

  useEffect(() => {
    Client.getEmployeeReference(from, to).then((newReferences: any) => {
      setReferenceArray(newReferences)
    })
  }, [from, to])

  const hasNonZeroValues = chosenMinutes.some(
    value => value !== 0 && value !== undefined,
  )

  return (
    <>
      <DashBoardCardTitle>
        {t('Employee preference').toUpperCase()}
      </DashBoardCardTitle>
      <EmployeeReferenceCardContainer>
        {referenceArray.length === 0 || !hasNonZeroValues ? (
          <NoEmployeeLabel>
            {t('There is no employee preference data available')}👤
          </NoEmployeeLabel>
        ) : (
          <>
            <Doughnut data={chartData} options={options} ref={chartRef} />
            <InfoIcon
              icon={faInfoCircle}
              title={t('Statistic based on TOTAL minutes per month')}
            />
          </>
        )}
      </EmployeeReferenceCardContainer>
    </>
  )
}
